import { RouterContextProvider } from 'react-router';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { crewClaimContext } from '~/app/features/crew/crewClaimMiddleware';
import {
  crewOrderContext,
  Order,
  orderContext,
} from '~/app/features/order-middleware/orderMiddleware';
import { shippingClaimContext } from '~/app/features/shipping-claim/new-claim/shippingClaimMiddleware';
import { settingsContext } from '~/app/settingsMiddleware';
import { AppStore } from '~/stores/app';
import { ClaimType } from '~/utils/constants';
import {
  makeLoader,
  matchShippingProtectionPath,
  redirect,
} from '~/utils/routing';
import { checkUsPostalCode, customerInfoSchema } from '~/utils/schemas';
import { isAddressPhoneRequired } from '~/utils/theme';

const pickCustomerInfo = (order: Order) => ({
  email: order.email,
  firstName: order.shippingAddress?.firstName,
  lastName: order.shippingAddress?.lastName,
  address: order.shippingAddress,
});

const handleAddressValidation = <Data>({
  customerInfo,
  requirePhone,
  onSuccess,
}: {
  customerInfo: unknown;
  requirePhone?: boolean;
  onSuccess: (info: z.infer<typeof customerInfoSchema>) => Data;
}) => {
  const schema = customerInfoSchema
    .extend({
      address: customerInfoSchema.shape.address.required({
        phone: requirePhone || undefined,
      }),
    })
    .check(checkUsPostalCode);

  return match(schema.safeParse(customerInfo))
    .with({ success: true }, ({ data }) => onSuccess(data))
    .with({ success: false }, () => redirect('edit'))
    .exhaustive();
};

const handleShippingProtectionRoute = (
  context: RouterContextProvider,
  appStore: AppStore,
) => {
  const order = context.get(orderContext);
  const shippingProtectionClaim = context.get(shippingClaimContext);

  return handleAddressValidation({
    customerInfo:
      shippingProtectionClaim.customerInfo ?? pickCustomerInfo(order),
    onSuccess: (customerInfo) => {
      appStore.dispatch(
        appStore.actions.shippingProtectionClaim.setCustomerInfo(customerInfo),
      );

      return {
        customerInfo,
        nextStep: '../review',
      };
    },
  });
};

const handleCrewClaimRoute = (
  context: RouterContextProvider,
  appStore: AppStore,
) => {
  const settings = context.get(settingsContext);
  const order = context.get(crewOrderContext);
  const crewClaim = context.get(crewClaimContext);

  return handleAddressValidation({
    customerInfo: crewClaim.customerInfo ?? pickCustomerInfo(order),
    requirePhone: isAddressPhoneRequired(settings.theme, crewClaim.claimType),
    onSuccess: (customerInfo) => {
      appStore.dispatch(
        appStore.actions.crewClaim.setCustomerInfo(customerInfo),
      );
      return {
        customerInfo,
        nextStep:
          crewClaim.claimType === ClaimType.warranty
            ? `../warranty-review`
            : `../return-methods`,
      };
    },
  });
};

export const { loader, useLoaderData } = makeLoader(
  ({ request, context }, appStore) =>
    matchShippingProtectionPath(request)
      ? handleShippingProtectionRoute(context, appStore)
      : handleCrewClaimRoute(context, appStore),
);
